import { useMemo } from "react";

import { TableViewIcon } from "@/assets/icons";
import ChartLoadingWrapper from "@/components/ui/chart/ChartLoadingWrapper";
import { buildSeriesFromData } from "@/components/ui/chart/utils";
import RetryErrorState from "@/components/ui/RetryErrorState";
import { MESSAGE } from "@/constants/message";
import useDatasetWidgetSummary from "@/hooks/dataset-widget/useDatasetWidgetSummary";
import { cn } from "@/lib/utils";
import { WidgetConfig, WidgetType } from "@/types/widget";

import DatasetWidgetChart, {
  UNGROUPED_CATEGORY_KEY
} from "./DatasetWidgetChart";
import DatasetWidgetTable, { WidgetTableRow } from "./DatasetWidgetTable";

import WidgetConfigurationPopover from "./widgetConfiguration/WidgetConfigurationPopover";

interface DatasetWidgetCardProps {
  widget: WidgetConfig;
  onOpenWidgetConfig: (id: string) => void;
  onRemoveWidget: (id: string) => void;
  /** Owned by the parent (see useWidgetTableView) so the mode survives the remount caused by expanding the panel. */
  isTableView: boolean;
  onToggleTableView: () => void;
  /** Defaults to widget.label when omitted. */
  name?: string;
  /** When provided the card fetches via the analysis-scoped API instead of the regular dataset API. */
  analysisId?: string;
  /** When true, disables the Remove action while a remove API call is in flight. */
  isRemovePending?: boolean;
}

export default function DatasetWidgetCard({
  widget,
  onOpenWidgetConfig,
  onRemoveWidget,
  isTableView,
  onToggleTableView,
  name,
  analysisId,
  isRemovePending = false
}: Readonly<DatasetWidgetCardProps>) {
  const {
    rawData,
    bigNumberData,
    groupedData,
    invalidWidget,
    analysisError,
    refetchAnalysisWidget,
    isChartLoading,
    isStructureChange,
    lineSeries,
    dimensionLabel
  } = useDatasetWidgetSummary(widget, { analysisId });

  const hasGroupedData = groupedData
    ? groupedData.rows.length > 0
    : rawData.length > 0;

  const isinValidWidgetData = invalidWidget || isStructureChange;

  // Big Number has no dimension to tabulate, so it stays chart-only.
  const canViewAsTable = widget.chart_type !== WidgetType.BIG_NUMBER;

  // The card keeps its instance when the widget is edited (same widget_id), so
  // the view mode must follow the current chart type — otherwise switching to
  // Big Number would strand the card in a table view it can no longer leave.
  const showTable = isTableView && canViewAsTable;

  const tableData = useMemo(() => {
    // The donut always renders rawData, so its table must do the same.
    const isGrouped = !!groupedData && widget.chart_type !== WidgetType.DONUT;

    if (isGrouped) {
      return {
        rows: groupedData.rows,
        categoryKey: groupedData.nameKey,
        series: groupedData.series
      };
    }

    const rows = rawData.map(
      (item): WidgetTableRow => ({
        label: item.label,
        value: Number(item.value)
      })
    );

    return {
      rows,
      categoryKey: UNGROUPED_CATEGORY_KEY,
      // Same fallback the charts apply when no measure series is available.
      series: lineSeries ?? buildSeriesFromData(rows, UNGROUPED_CATEGORY_KEY)
    };
  }, [groupedData, rawData, lineSeries, widget.chart_type]);

  return (
    <div className="border border-gainsboro rounded-lg p-3">
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <span className="text-base font-semibold text-txtHeading">
            {name ?? widget.label}
          </span>
          <div className="flex flex-row items-center gap-3">
            {canViewAsTable && (
              <button
                aria-label={isTableView ? "View as chart" : "View as table"}
                aria-pressed={isTableView}
                onClick={onToggleTableView}
                type="button"
              >
                <TableViewIcon
                  className={cn(
                    "h-6 w-6",
                    isTableView ? "fill-teal-500" : "fill-txtBody"
                  )}
                />
              </button>
            )}
            <WidgetConfigurationPopover
              onOpenWidgetConfig={() => onOpenWidgetConfig(widget.widget_id)}
              onRemoveWidget={() => onRemoveWidget(widget.widget_id)}
              isRemovePending={isRemovePending}
            />
          </div>
        </div>
        {isinValidWidgetData && (
          <div className="flex flex-col justify-center gap-2 items-center h-[400px] px-4">
            <p className="text-base text-txtHeading font-semibold text-center">
              {MESSAGE.WIDGET_CARD_UNAVAILABLE_DATA_SOURCE_TITLE}
            </p>
            <p className="text-sm text-txtBody text-center">
              {invalidWidget
                ? MESSAGE.WIDGET_CARD_UNAVAILABLE_DATA_SOURCE_DESCRIPTION
                : MESSAGE.WIDGET_CARD_STRUCTURE_CHANGE_DESCRIPTION}
            </p>
          </div>
        )}
        {analysisError && analysisId && !isinValidWidgetData && (
          <RetryErrorState onRetry={refetchAnalysisWidget} />
        )}

        {!isinValidWidgetData && !analysisError && (
          <ChartLoadingWrapper
            isLoading={isChartLoading}
            chartType={widget.chart_type}
          >
            {((widget.chart_type !== WidgetType.BIG_NUMBER &&
              !hasGroupedData) ||
              (widget.chart_type === WidgetType.BIG_NUMBER &&
                !bigNumberData)) && (
              <div className="flex-center h-[400px] px-4">
                <p className="text-base text-txtHeading font-semibold text-center">
                  {MESSAGE.WIDGET_CARD_NO_DATA_AVAILABLE}
                </p>
              </div>
            )}
            {/* The empty state above already covers the no-data case, so the
                table only renders once there is something to tabulate. */}
            {showTable ? (
              tableData.rows.length > 0 && (
                <DatasetWidgetTable
                  rows={tableData.rows}
                  categoryKey={tableData.categoryKey}
                  categoryHeader={dimensionLabel || tableData.categoryKey}
                  series={tableData.series}
                  aggregation={widget.aggregation}
                />
              )
            ) : (
              <DatasetWidgetChart
                widget={widget}
                rawData={rawData}
                groupedData={groupedData}
                lineSeries={lineSeries}
                bigNumberData={bigNumberData}
              />
            )}
          </ChartLoadingWrapper>
        )}
      </div>
    </div>
  );
}
